import React, { useCallback, useEffect, useState } from "react";
import { ObjectInspectSidebar } from "./ObjectInspectSidebar";
import { useSidebarContext } from "../SidebarContext";
import { useObjectSettings } from "../contexts/ObjectSettingsProvider";
import notificationsCenter from "../../singleton/NotificationsCenter";
import bwipjs from "@bwip-js/browser";

import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Button,
  makeStyles,
  Text,
  Caption1,
  Divider,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Input,
  tokens
} from "@fluentui/react-components";
import { Dismiss24Regular, CopyRegular, ArrowDownloadFilled } from "@fluentui/react-icons";
import configs from "../../utils/configs";
import { CAMERA_MODE_INSPECT } from "../../systems/camera-system";
import { useObjectList } from "./useObjectList";
import { FormattedMessage } from "react-intl";
import { extractPersistenceFileId } from "../../utils/persistence-url-utils";
import { ResponsiveOverlayDrawer } from "../drawer/ResponsiveOverlayDrawer";

const useStyles = makeStyles({
  drawer: {
    width: "463px"
  },
  headerDrawer: {
    display: " flex",
    padding: " var(--Font-size-600, 24px) 16px 12px var(--Font-size-600, 24px)",
    alignItems: " flex-start",
    gap: " 8px",
    alignSelf: " stretch",
    backgroundColor: tokens.colorNeutralBackground1
  },
  drawerBody: {
    paddingTop: "20px"
  },
  search: {
    width: "100%"
  },
  section: {
    display: " flex",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "2px"
  },
  sectionHeader: {
    display: " flex",
    padding: " 8px 0px",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sectionHeaderText: {
    color: "var(--Gray-3, #828282)"
  },
  sectionContent: {
    width: "100%",
    display: " flex",
    padding: " 4px",
    alignItems: " center",
    height: "48px",
    borderRadius: "4px",
    justifyContent: "space-between",
    gap: " 8px",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover
    }
  },
  persona: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "32px",
    "& .fui-Avatar__image": {
      width: "32px",
      height: "32px"
    },
    "& .fui-Avatar": {
      height: "32px"
    },
    "& .fui-Persona": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "32px"
    }
  },
  icons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& > *": {
      cursor: "pointer"
    }
  },
  divider: {
    padding: "5px 0px 4px 0px"
  },
  redIcon: {
    color: "var(--Red, #EB5757)"
  },
  avatarGroupItem: {
    backgroundColor: tokens.colorNeutralBackground3,
    "& img": {
      backgroundColor: tokens.colorNeutralBackground3
    }
  },
  avatarGroupItemAll: {
    "& img": {
      backgroundColor: tokens.colorNeutralBackground3
    }
  },
  qrCodeContainer: {
    display: " flex",
    alignItems: " flex-end",
    gap: " 6px"
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: "var(--Font-size-100, 10px)"
  },
  divider: {
    padding: "5px 0px 4px 0px",
    marginTop: "20px",
    marginBottom: "20px"
  },
  accordionHeaderItem: {
    "& button": {
      paddingLeft: "0px"
    }
  }
});

export function ArtworkInfoSidebarContainer({ hubChannel, scene }) {
  const styles = useStyles();
  const [arUrl, setARUrl] = useState(null);
  const [artworkInfo, setArtworkInfo] = useState(null);
  const [assetUuid, setAssetUuid] = useState(null);
  const [isExternalAsset, setIsExternalAsset] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const { setSidebar } = useSidebarContext();

  const { selectedObject } = useObjectList();
  const qrCanvasRef = React.useRef(null); // Ref to the QR code canvas

  const [isOpen, setIsOpen] = React.useState(true);
  const artworkQREl = selectedObject?.el;

  const metadata = artworkInfo?.metadata;

  const copyUrl = url => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        notificationsCenter.success("URL copied to clipboard");
      })
      .catch(err => {
        console.error("Failed to copy: ", err);
      });
  };

  const copyQRCode = () => {
    const canvas = qrCanvasRef.current;
    if (!canvas) return;

    canvas.toBlob(blob => {
      // eslint-disable-next-line no-undef
      const item = new ClipboardItem({ "image/png": blob });
      navigator.clipboard
        .write([item])
        .then(() => {
          notificationsCenter.success("QR Code image copied to clipboard");
        })
        .catch(err => {
          console.error("Failed to copy QR code: ", err);
          notificationsCenter.error("Failed to copy QR code");
        });
    });
  };

  const copyArUrl = () => {
    if (arUrl) {
      copyUrl(arUrl);
    }
  };

  const downloadQRCode = () => {
    const canvas = qrCanvasRef.current;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = "qrcode.png";
    link.click();
  };

  const generateQRCode = url => {
    bwipjs.toCanvas(qrCanvasRef.current, {
      bcid: "qrcode",
      text: url,
      scaleX: 2,
      scaleY: 2,
      includetext: false,
      textxalign: "center",
      paddingwidth: 3,
      paddingheight: 3,
      backgroundcolor: "#fff"
    });
    // setQRCanvas(canvas);
  };

  useEffect(() => {
    // Priority 1: Use previewUrl if provided
    if (previewUrl) {
      setARUrl(previewUrl);
      return;
    }

    // Priority 2: Use asset UUID only if asset is internal (from our platform)
    if (assetUuid && !isExternalAsset) {
      const arUrl = `${configs["editorUrl"]}/assets/preview?fileId=${assetUuid}`;
      setARUrl(arUrl);
      return;
    }

    // Priority 3: Fall back to qrCodeId from artwork component (for external assets without previewUrl)
    const qrCodeId = artworkQREl?.components["artwork"]?.data.qrCodeId;
    if (qrCodeId) {
      const arUrl = `${configs["AR_EDITOR"]}/view/${qrCodeId}`;
      setARUrl(arUrl);
      return;
    }

    // No AR URL available
    setARUrl(null);
  }, [selectedObject, artworkQREl, assetUuid, isExternalAsset, previewUrl]);

  useEffect(() => {
    // Get artwork info from artwork component
    const artworkData = selectedObject?.el?.components["artwork"]?.data?.artworkInfo;
    if (artworkData) {
      setArtworkInfo(artworkData);
    }

    // Get metadata from asset-metadata component when available
    const assetMetadata = selectedObject?.el?.components["asset-metadata"];
    const mediaLoaderData = selectedObject?.el?.components["media-loader"]?.data;
    const persistenceFileId =
      extractPersistenceFileId(mediaLoaderData?.assetSrc, { persistenceApiUrl: configs.PERSISTENCE_API_URL }) ||
      extractPersistenceFileId(mediaLoaderData?.src, { persistenceApiUrl: configs.PERSISTENCE_API_URL });

    if (assetMetadata) {
      setAssetUuid(assetMetadata.data.assetId || persistenceFileId || null);
      setIsExternalAsset(assetMetadata.data.isExternalAsset || false);
      setPreviewUrl(assetMetadata.data.previewUrl || null);
    } else {
      setAssetUuid(persistenceFileId || null);
      setIsExternalAsset(false);
      setPreviewUrl(null);
    }
  }, [selectedObject]);

  useEffect(() => {
    if (arUrl) {
      generateQRCode(arUrl);
    }
  }, [arUrl]);

  const onClose = useCallback(() => {
    setIsOpen(false);
    setSidebar(null);
    window.APP.scene.systems["hubs-systems"].cameraSystem.uninspect();
  }, []);

  const cameraMode = window.APP.scene.systems["hubs-systems"].cameraSystem.mode;

  React.useEffect(() => {
    if (cameraMode != CAMERA_MODE_INSPECT) {
      onClose();
    }
  }, [cameraMode]);

  return (
    <ResponsiveOverlayDrawer
      modalType="non-modal"
      desktopPosition="end"
      as="aside"
      open={isOpen}
      className={styles.drawer}
    >
      <DrawerHeader className={styles.headerDrawer}>
        <DrawerHeaderTitle
          style={{ width: "100%" }}
          action={<Button onClick={onClose} appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />}
        >
          <FormattedMessage id="artwork-info-sidebar.title" defaultMessage="Metadata" />
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody}>
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {arUrl && (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <Text size={"small"}>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  <FormattedMessage id="artwork-info-sidebar.scan-to-view-in-ar" defaultMessage="Scan to view in AR" />
                </Caption1>
              </Text>

              <div className={styles.qrCodeContainer}>
                <canvas
                  id="qr-info"
                  ref={qrCanvasRef}
                  style={{ borderRadius: "4px", border: `1px solid ${tokens.colorNeutralStroke1}` }}
                ></canvas>
                <div className={styles.buttonsContainer}>
                  <Button appearance="outline" icon={<CopyRegular />} onClick={copyQRCode} />
                  <Button appearance="primary" icon={<ArrowDownloadFilled />} onClick={downloadQRCode} />
                </div>
              </div>

              <Input
                readOnly
                value={arUrl}
                contentAfter={<CopyRegular style={{ cursor: "pointer" }} onClick={copyArUrl} />}
                appearance="filled-lighter"
              />
            </div>
          )}
          <Text style={{ fontSize: "18px" }} weight="semibold">
            {artworkInfo?.author}
          </Text>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <Text size={400} weight="semibold">
              {artworkInfo?.title}
              {artworkInfo?.artworkDate && <>, {artworkInfo?.artworkDate}</>}
            </Text>
            <Text size={400}>
              {artworkInfo?.medium == "Custom" ? artworkInfo?.metadata?.customMedium : artworkInfo?.medium}
            </Text>
            {artworkInfo?.dimensions && <Text size={400}>{artworkInfo?.dimensions}</Text>}
          </div>
          <Text size={400}>{artworkInfo?.description}</Text>
        </div>
        {(metadata?.techniqueUsed ||
          metadata?.currentPhysicalWork ||
          metadata?.artistBio ||
          artworkInfo?.artistStatement ||
          metadata?.aiToolUsed ||
          metadata?.workCreatedByAI) && (
          <>
            <Divider className={styles.divider} />
            <Accordion collapsible multiple>
              <AccordionItem value="1">
                <AccordionHeader className={styles.accordionHeaderItem}>
                  <Text size={200} style={{ lineHeight: "20px" }}>
                    More Info
                  </Text>
                </AccordionHeader>
                <AccordionPanel style={{ display: "flex", flexDirection: "column", marginLeft: "4px" }}>
                  {(metadata?.techniqueUsed || metadata?.currentPhysicalWork) && (
                    <>
                      <Text size={400} weight="semibold">
                        Work Information
                      </Text>
                      <Accordion multiple openItems={["2", "3"]} defaultValue={["2", "3"]}>
                        {metadata?.techniqueUsed && (
                          <AccordionItem value="2">
                            <AccordionHeader
                              style={{ marginLeft: "-7px" }}
                              className={styles.accordionHeaderItem}
                              expandIcon={<></>}
                            >
                              <Text size={200} style={{ lineHeight: "20px" }}>
                                Digitization technique used
                              </Text>
                            </AccordionHeader>
                            <AccordionPanel
                              style={{ display: "flex", flexDirection: "column", gap: "20px", marginLeft: "4px" }}
                            >
                              <Text size={300} weight="semibold">
                                {metadata?.techniqueUsed}
                              </Text>
                            </AccordionPanel>
                          </AccordionItem>
                        )}

                        {metadata?.currentPhysicalWork && (
                          <AccordionItem value="3">
                            <AccordionHeader
                              style={{ marginLeft: "-7px" }}
                              className={styles.accordionHeaderItem}
                              expandIcon={<></>}
                            >
                              <Text size={200} style={{ lineHeight: "20px" }}>
                                Current State of Physical Works
                              </Text>
                            </AccordionHeader>
                            <AccordionPanel
                              style={{ display: "flex", flexDirection: "column", gap: "20px", marginLeft: "4px" }}
                            >
                              <Text size={300} weight="semibold">
                                {metadata?.currentPhysicalWork}
                              </Text>
                            </AccordionPanel>
                          </AccordionItem>
                        )}
                      </Accordion>
                      <Divider className={styles.divider} />
                    </>
                  )}
                  {(metadata?.artistBio || artworkInfo?.artistStatement) && (
                    <>
                      <Text size={400} weight="semibold">
                        Artist Information
                      </Text>
                      <Accordion multiple openItems={["2", "3"]} defaultValue={["2", "3"]}>
                        {metadata?.artistBio && (
                          <AccordionItem value="2">
                            <AccordionHeader
                              style={{ marginLeft: "-7px" }}
                              className={styles.accordionHeaderItem}
                              expandIcon={<></>}
                            >
                              <Text size={200} style={{ lineHeight: "20px" }}>
                                Bio
                              </Text>
                            </AccordionHeader>
                            <AccordionPanel
                              style={{ display: "flex", flexDirection: "column", gap: "20px", marginLeft: "4px" }}
                            >
                              <Text size={300}>{metadata?.artistBio}</Text>
                            </AccordionPanel>
                          </AccordionItem>
                        )}
                        {artworkInfo?.artistStatement && (
                          <AccordionItem value="3">
                            <AccordionHeader
                              style={{ marginLeft: "-7px" }}
                              className={styles.accordionHeaderItem}
                              expandIcon={<></>}
                            >
                              <Text size={200} style={{ lineHeight: "20px" }}>
                                Statement
                              </Text>
                            </AccordionHeader>
                            <AccordionPanel
                              style={{ display: "flex", flexDirection: "column", gap: "20px", marginLeft: "4px" }}
                            >
                              <Text size={300}>{artworkInfo?.artistStatement}</Text>
                            </AccordionPanel>
                          </AccordionItem>
                        )}
                      </Accordion>
                      <Divider className={styles.divider} />
                    </>
                  )}
                  {metadata?.workCreatedByAI && metadata?.aiToolUsed && (
                    <>
                      <Text size={400} weight="semibold">
                        AI-generated Artwork
                      </Text>
                      <Accordion multiple openItems={["2", "3"]} defaultValue={["2", "3"]}>
                        {metadata?.aiToolUsed && (
                          <AccordionItem value="3">
                            <AccordionHeader
                              style={{ marginLeft: "-7px" }}
                              className={styles.accordionHeaderItem}
                              expandIcon={<></>}
                            >
                              <Text size={200} style={{ lineHeight: "20px" }}>
                                AI Tool Used
                              </Text>
                            </AccordionHeader>
                            <AccordionPanel
                              style={{ display: "flex", flexDirection: "column", gap: "20px", marginLeft: "4px" }}
                            >
                              <Text size={300}>{metadata?.aiToolUsed}</Text>
                            </AccordionPanel>
                          </AccordionItem>
                        )}
                      </Accordion>
                    </>
                  )}
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </>
        )}
      </DrawerBody>
    </ResponsiveOverlayDrawer>
  );
}
